'use client'

import * as React from 'react'

import { track } from '@/lib/gtm'

type ScrollDepthTrackerProps = {
  /**
   * Scroll depth threshold expressed as a fraction (0-1).
   */
  threshold?: number
  /**
   * GTM event name.
   */
  event?: string
  /**
   * Context label for analytics payload.
   */
  place?: string
}

export function ScrollDepthTracker({
  threshold = 0.5,
  event = 'scroll_depth',
  place = 'home',
}: ScrollDepthTrackerProps) {
  const hasTrackedRef = React.useRef(false)

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    let ticking = false

    const checkDepth = () => {
      if (hasTrackedRef.current) {
        ticking = false
        return
      }

      const { scrollHeight, clientHeight } = document.documentElement
      const viewportBottom = window.scrollY + window.innerHeight
      const maxScrollable = scrollHeight - clientHeight
      const ratio = maxScrollable <= 0 ? 1 : Math.min(viewportBottom / scrollHeight, 1)

      if (ratio >= threshold) {
        hasTrackedRef.current = true
        track(event, {
          depth: Math.round(threshold * 100),
          place,
        })
        window.removeEventListener('scroll', handleScroll)
      }

      ticking = false
    }

    const handleScroll = () => {
      if (!ticking) {
        ticking = true
        window.requestAnimationFrame(checkDepth)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    // Check immediately in case the page loads already past the threshold.
    checkDepth()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [event, place, threshold])

  return null
}
